// src/utils/countryConfig.js

/**
 * Configuration for US and UK specific features
 * This drives the entire country-specific functionality
 */

export const COUNTRIES = {
  US: 'US',
  UK: 'UK'
};

export const countryConfig = {
  US: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currency: {
      symbol: '$',
      code: 'USD',
      name: 'US Dollar'
    },
    taxYear: {
      start: '01-01',
      end: '12-31'
    },
    accounts: [
      {
        id: '401k',
        name: '401(k)',
        type: 'retirement',
        description: 'Employer-sponsored retirement account',
        contributionLimit2024: 23000,
        catchUpLimit: 7500, // Age 50+
        employerMatchLimit: 69000,
        taxBenefit: 'Pre-tax contributions reduce taxable income',
        withdrawalAge: 59.5,
        rmdAge: 73,
        features: [
          'Employer matching',
          'Pre-tax contributions',
          'Tax-deferred growth',
          'Early withdrawal penalties'
        ]
      },
      {
        id: 'roth_ira',
        name: 'Roth IRA',
        type: 'retirement',
        description: 'Individual retirement account with tax-free growth',
        contributionLimit2024: 7000,
        catchUpLimit: 1000,
        incomeLimitSingle: 161000,
        incomeLimitMarried: 240000,
        taxBenefit: 'Tax-free withdrawals in retirement',
        withdrawalAge: 59.5,
        rmdAge: null, // No RMD for Roth IRA
        features: [
          'Tax-free growth',
          'Tax-free withdrawals',
          'No RMDs',
          'Income limits apply'
        ]
      },
      {
        id: 'traditional_ira',
        name: 'Traditional IRA',
        type: 'retirement',
        description: 'Individual retirement account with tax deductions',
        contributionLimit2024: 7000,
        catchUpLimit: 1000,
        taxBenefit: 'Tax deductions on contributions',
        withdrawalAge: 59.5,
        rmdAge: 73,
        features: [
          'Tax deductions',
          'Tax-deferred growth',
          'RMDs required',
          'Early withdrawal penalties'
        ]
      },
      {
        id: 'sep_ira',
        name: 'SEP IRA',
        type: 'retirement',
        description: 'Simplified Employee Pension for self-employed',
        contributionLimit2024: 69000,
        percentageLimit: 25, // 25% of compensation
        taxBenefit: 'High contribution limits for self-employed',
        withdrawalAge: 59.5,
        rmdAge: 73,
        features: [
          'High contribution limits',
          'Easy to set up',
          'Flexible contributions',
          'Self-employed friendly'
        ]
      },
      {
        id: 'hsa',
        name: 'HSA',
        type: 'health',
        description: 'Health Savings Account with triple tax advantage',
        contributionLimit2024Individual: 4150,
        contributionLimit2024Family: 8300,
        catchUpLimit: 1000, // Age 55+
        taxBenefit: 'Triple tax advantage: deductible, tax-free growth, tax-free medical withdrawals',
        features: [
          'Triple tax advantage',
          'Rolls over year to year',
          'Investment options',
          'Retirement healthcare savings'
        ]
      },
      {
        id: '529',
        name: '529 Plan',
        type: 'education',
        description: 'College savings plan with tax advantages',
        contributionLimit2024: 'Varies by state (typically $300k-$500k lifetime)',
        taxBenefit: 'State tax deductions, tax-free growth for education',
        features: [
          'State tax deductions',
          'Tax-free growth',
          'Education expenses',
          'Can change beneficiary'
        ]
      },
      {
        id: 'brokerage',
        name: 'Taxable Brokerage',
        type: 'taxable',
        description: 'Standard investment account with no limits',
        contributionLimit2024: 'No limit',
        taxBenefit: 'Capital gains tax rates (0%, 15%, 20%)',
        features: [
          'No contribution limits',
          'Full liquidity',
          'Tax-loss harvesting',
          'Capital gains treatment'
        ]
      },
      {
        id: 'crypto_exchange',
        name: 'Crypto Exchange Account',
        type: 'crypto',
        description: 'Cryptocurrency exchange account (Coinbase, Kraken, Binance, etc.)',
        contributionLimit2024: 'No limit',
        taxBenefit: 'Capital gains tax treatment on crypto sales',
        features: [
          'Multiple cryptocurrencies',
          'Exchange platform access',
          'Capital gains reporting required',
          'Subject to exchange regulations'
        ]
      },
      {
        id: 'crypto_wallet',
        name: 'Private Crypto Wallet',
        type: 'crypto',
        description: 'Self-custody cryptocurrency wallet (hardware or software)',
        contributionLimit2024: 'No limit',
        taxBenefit: 'Capital gains tax treatment on crypto sales',
        features: [
          'Full self-custody',
          'Hardware or software wallet',
          'Capital gains reporting required',
          'Private key management'
        ]
      },
      {
        id: 'savings_account',
        name: 'Savings Account',
        type: 'cash',
        description: 'High-yield savings account for emergency funds and short-term goals',
        contributionLimit2024: 'No limit',
        fdic_insurance: 250000,
        taxBenefit: 'Interest income taxed as ordinary income',
        features: [
          'FDIC insured up to $250,000',
          'Competitive interest rates',
          'Easy access to funds',
          'No investment risk'
        ]
      },
      {
        id: 'checking_account',
        name: 'Checking Account',
        type: 'cash',
        description: 'Standard checking account for daily transactions and bill payments',
        contributionLimit2024: 'No limit',
        fdic_insurance: 250000,
        taxBenefit: 'Interest income (if any) taxed as ordinary income',
        features: [
          'FDIC insured up to $250,000',
          'Debit card access',
          'Bill pay and transfers',
          'Daily transaction account'
        ]
      }
    ],
    taxRates: {
      capitalGains: {
        short: 'Ordinary income tax rates (10%-37%)',
        long: [
          { rate: 0, threshold: 0 },
          { rate: 15, threshold: 44625 },
          { rate: 20, threshold: 492300 }
        ]
      },
      income: [
        { rate: 10, threshold: 0 },
        { rate: 12, threshold: 11000 },
        { rate: 22, threshold: 44725 },
        { rate: 24, threshold: 95375 },
        { rate: 32, threshold: 182100 },
        { rate: 35, threshold: 231250 },
        { rate: 37, threshold: 578125 }
      ]
    }
  },

  UK: {
    code: 'UK',
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: {
      symbol: '£',
      code: 'GBP',
      name: 'British Pound'
    },
    taxYear: {
      start: '04-06', // April 6
      end: '04-05'   // April 5 next year
    },
    accounts: [
      {
        id: 'stocks_shares_isa',
        name: 'Stocks & Shares ISA',
        type: 'isa',
        description: 'Tax-free investment account',
        contributionLimit2024: 20000,
        taxBenefit: 'Tax-free growth and withdrawals',
        features: [
          '£20,000 annual allowance',
          'Tax-free growth',
          'Tax-free withdrawals',
          'Multiple ISA providers allowed'
        ]
      },
      {
        id: 'cash_isa',
        name: 'Cash ISA',
        type: 'isa',
        description: 'Tax-free savings account',
        contributionLimit2024: 20000, // Part of total ISA allowance
        taxBenefit: 'Tax-free interest',
        features: [
          'Part of ISA allowance',
          'Tax-free interest',
          'Easy access options',
          'Fixed term options'
        ]
      },
      {
        id: 'lifetime_isa',
        name: 'Lifetime ISA',
        type: 'isa',
        description: 'ISA for first home or retirement with government bonus',
        contributionLimit2024: 4000,
        governmentBonus: 1000, // 25% up to £1,000
        ageLimit: { min: 18, max: 39 },
        taxBenefit: '25% government bonus on contributions',
        withdrawalPenalty: 25, // If not for first home or retirement
        features: [
          '25% government bonus',
          'First home purchase',
          'Retirement savings',
          'Withdrawal penalties apply'
        ]
      },
      {
        id: 'junior_isa',
        name: 'Junior ISA',
        type: 'isa',
        description: 'Tax-free savings for children',
        contributionLimit2024: 9000,
        ageLimit: { min: 0, max: 17 },
        taxBenefit: 'Tax-free growth until age 18',
        features: [
          '£9,000 annual limit',
          'For children under 18',
          'Parents manage until 16',
          'Accessible at 18'
        ]
      },
      {
        id: 'workplace_pension',
        name: 'Workplace Pension',
        type: 'pension',
        description: 'Employer pension scheme',
        contributionLimit2024: 60000,
        minimumEmployerContribution: 3,
        minimumEmployeeContribution: 5,
        taxBenefit: 'Tax relief on contributions + employer contributions',
        features: [
          'Employer contributions',
          'Tax relief',
          'Auto-enrollment',
          'Annual allowance £60,000'
        ]
      },
      {
        id: 'sipp',
        name: 'SIPP',
        type: 'pension',
        description: 'Self-Invested Personal Pension',
        contributionLimit2024: 60000,
        taxBenefit: 'Tax relief on contributions, wide investment choices',
        features: [
          'Wide investment choice',
          'Tax relief 20-45%',
          'Consolidate pensions',
          'Flexible withdrawals'
        ]
      },
      {
        id: 'defined_benefit_pension',
        name: 'Defined Benefit Pension',
        type: 'pension',
        description: 'Final salary pension scheme (common for civil service)',
        contributionLimit2024: 60000, // Part of annual allowance
        taxBenefit: 'Guaranteed income in retirement based on salary and service',
        features: [
          'Guaranteed retirement income',
          'Based on salary and service years',
          'Inflation-linked increases',
          'Common for public sector/civil service'
        ]
      },
      {
        id: 'gia',
        name: 'General Investment Account',
        type: 'taxable',
        description: 'Standard investment account',
        contributionLimit2024: 'No limit',
        capitalGainsAllowance: 3000,
        dividendAllowance: 500,
        taxBenefit: 'Annual CGT and dividend allowances',
        features: [
          'No contribution limits',
          '£3,000 CGT allowance',
          '£500 dividend allowance',
          'Bed and ISA options'
        ]
      },
      {
        id: 'crypto_exchange',
        name: 'Crypto Exchange Account',
        type: 'crypto',
        description: 'Cryptocurrency exchange account (Coinbase, Kraken, Binance, etc.)',
        contributionLimit2024: 'No limit',
        capitalGainsAllowance: 3000,
        taxBenefit: '£3,000 CGT allowance on crypto gains',
        features: [
          'Multiple cryptocurrencies',
          'Exchange platform access',
          '£3,000 CGT allowance',
          'Subject to FCA regulations'
        ]
      },
      {
        id: 'crypto_wallet',
        name: 'Private Crypto Wallet',
        type: 'crypto',
        description: 'Self-custody cryptocurrency wallet (hardware or software)',
        contributionLimit2024: 'No limit',
        capitalGainsAllowance: 3000,
        taxBenefit: '£3,000 CGT allowance on crypto gains',
        features: [
          'Full self-custody',
          'Hardware or software wallet',
          '£3,000 CGT allowance',
          'Private key management'
        ]
      },
      {
        id: 'savings_account',
        name: 'Savings Account',
        type: 'cash',
        description: 'High-interest savings account for emergency funds and short-term goals',
        contributionLimit2024: 'No limit',
        fscs_protection: 85000,
        taxBenefit: 'Personal Savings Allowance (£1,000 basic rate, £500 higher rate)',
        features: [
          'FSCS protected up to £85,000',
          'Competitive interest rates',
          'Easy access or fixed term options',
          'Personal Savings Allowance applies'
        ]
      },
      {
        id: 'current_account',
        name: 'Current Account',
        type: 'cash',
        description: 'Standard current account for daily transactions and bill payments',
        contributionLimit2024: 'No limit',
        fscs_protection: 85000,
        taxBenefit: 'Interest (if any) covered by Personal Savings Allowance',
        features: [
          'FSCS protected up to £85,000',
          'Debit card access',
          'Direct debits and standing orders',
          'Overdraft facilities available'
        ]
      }
    ],
    taxRates: {
      capitalGains: [
        { rate: 10, threshold: 0, band: 'basic' },
        { rate: 20, threshold: 50270, band: 'higher' }
      ],
      dividends: [
        { rate: 8.75, threshold: 0, band: 'basic' },
        { rate: 33.75, threshold: 50270, band: 'higher' },
        { rate: 39.35, threshold: 125140, band: 'additional' }
      ],
      income: [
        { rate: 0, threshold: 0, band: 'personal allowance' },
        { rate: 20, threshold: 12570, band: 'basic' },
        { rate: 40, threshold: 50270, band: 'higher' },
        { rate: 45, threshold: 125140, band: 'additional' }
      ]
    }
  }
};

/**
 * Get account types for a specific country
 */
export const getAccountTypes = (country) => {
  return countryConfig[country]?.accounts || [];
};

/**
 * Get specific account type details
 */
export const getAccountTypeDetails = (country, accountId) => {
  const accounts = getAccountTypes(country);
  return accounts.find(acc => acc.id === accountId);
};

/**
 * Format currency based on country
 */
export const formatCurrency = (amount, country) => {
  const config = countryConfig[country];
  if (!config) return amount;

  return new Intl.NumberFormat(country === 'US' ? 'en-US' : 'en-GB', {
    style: 'currency',
    currency: config.currency.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Format currency with specific currency code
 * Used for multi-currency support
 *
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code (USD, GBP, EUR)
 * @param {boolean} compact - Use compact notation for large numbers
 * @returns {string} Formatted currency string
 */
export const formatCurrencyWithCode = (amount, currencyCode, compact = false) => {
  const locale = currencyCode === 'USD' ? 'en-US' : currencyCode === 'GBP' ? 'en-GB' : 'de-DE';

  const options = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  };

  if (compact && Math.abs(amount) >= 1000) {
    options.notation = 'compact';
    options.compactDisplay = 'short';
  }

  return new Intl.NumberFormat(locale, options).format(amount);
};

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (country) => {
  return countryConfig[country]?.currency.symbol || '$';
};

/**
 * Get currency symbol by currency code
 *
 * @param {string} currencyCode - Currency code (USD, GBP, EUR)
 * @returns {string} Currency symbol
 */
export const getCurrencySymbolByCode = (currencyCode) => {
  const symbols = {
    USD: '$',
    GBP: '£',
    EUR: '€'
  };
  return symbols[currencyCode] || currencyCode;
};

/**
 * Get currency code from country
 *
 * @param {string} country - Country code (US, UK)
 * @returns {string} Currency code (USD, GBP)
 */
export const getCurrencyCode = (country) => {
  return countryConfig[country]?.currency.code || 'USD';
};

/**
 * Check if contribution limit exceeded
 */
export const checkContributionLimit = (country, accountType, currentContributions, newContribution) => {
  const accountDetails = getAccountTypeDetails(country, accountType);
  if (!accountDetails) return { valid: false, message: 'Account type not found' };

  let limit = 0;
  
  // Get the appropriate limit
  if (accountDetails.contributionLimit2024) {
    limit = accountDetails.contributionLimit2024;
  } else if (accountDetails.contributionLimit2024Individual) {
    limit = accountDetails.contributionLimit2024Individual;
  }

  const total = currentContributions + newContribution;
  
  if (total > limit) {
    return {
      valid: false,
      message: `Contribution would exceed annual limit of ${formatCurrency(limit, country)}`,
      limit,
      current: currentContributions,
      remaining: limit - currentContributions
    };
  }

  return {
    valid: true,
    message: 'Contribution within limits',
    limit,
    current: currentContributions,
    remaining: limit - total
  };
};

export default countryConfig;
